'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [currentYear, setCurrentYear] = useState<number | null>(null);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    services: [
      { label: 'Minerals Transport', href: '/services-overview' },
      { label: 'Heavy Equipment', href: '/services-overview' },
      { label: 'Cross-Border Logistics', href: '/services-overview' },
      { label: 'Customs Brokerage', href: '/services-overview' }
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Leadership', href: '/about' },
      { label: 'Certifications', href: '/about' },
      { label: 'Careers', href: '/about' }
    ],
    resources: [
      { label: 'Regional Coverage', href: '/regional-coverage' },
      { label: 'Safety & Compliance', href: '/safety-and-compliance' },
      { label: 'Contact Us', href: '/contact-and-quotes' },
      { label: 'Get Quote', href: '/contact-and-quotes' }
    ]
  };

  const socialLinks = [
    { icon: 'GlobeAltIcon', label: 'Website', href: '#' },
    { icon: 'EnvelopeIcon', label: 'Email', href: 'mailto:mylesdrivelc@gmail.com' },
    { icon: 'PhoneIcon', label: 'Phone', href: 'tel:+243981944976' }
  ];

  return (
    <footer className={`bg-primary text-white ${className}`}>
      <div className="container mx-auto px-4 py-12 lg:py-16">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link href="/homepage" className="inline-flex items-center space-x-2 mb-4 group">
              <div className="flex flex-col">
                <span className="font-headline font-bold text-lg text-white">MylesDrive</span>
                <span className="font-body text-xs text-white/70">Logistics Hub</span>
              </div>
            </Link>
            <p className="font-body text-sm text-white/80 leading-relaxed mb-4">
              Africa's premier logistics partner for mining operations, heavy equipment transport, and cross-border trade.
            </p>
            <div className="flex items-center space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="bg-white/10 hover:bg-accent p-2 rounded-lg transition-all duration-300 hover:scale-110 group"
                  aria-label={social.label}
                >
                  <Icon name={social.icon as any} size={20} className="text-white group-hover:text-primary" />
                </a>
              ))}
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="font-headline font-bold text-base text-white mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="font-body text-sm text-white/70 hover:text-accent transition-colors duration-300 inline-flex items-center group"
                  >
                    <Icon name="ChevronRightIcon" size={14} className="mr-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-headline font-bold text-base text-white mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="font-body text-sm text-white/70 hover:text-accent transition-colors duration-300 inline-flex items-center group"
                  >
                    <Icon name="ChevronRightIcon" size={14} className="mr-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h3 className="font-headline font-bold text-base text-white mb-4">Resources</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="font-body text-sm text-white/70 hover:text-accent transition-colors duration-300 inline-flex items-center group"
                  >
                    <Icon name="ChevronRightIcon" size={14} className="mr-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="font-body text-sm text-white/70 text-center md:text-left">
              {currentYear !== null ? `© ${currentYear}` : '©'} MylesDrive Logistics Hub. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link href="#" className="font-body text-sm text-white/70 hover:text-accent transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link href="#" className="font-body text-sm text-white/70 hover:text-accent transition-colors duration-300">
                Terms of Service
              </Link>
              <Link href="#" className="font-body text-sm text-white/70 hover:text-accent transition-colors duration-300">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;